import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Checkbox,
  FormControlLabel,
  Link as MuiLink,
  CircularProgress,
  Collapse,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import OAuthButtons from '../../components/auth/OAuthButtons';
import { apiService } from '../../services/api';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaVerifying, setMfaVerifying] = useState(false);

  // Lockout state
  const [lockoutUntil, setLockoutUntil] = useState<string | null>(null);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);

  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Check for success message from auth callback
  React.useEffect(() => {
    const state = location.state as any;
    if (state?.message) {
      setSuccessMessage(state.message);
      // Clear the state to prevent showing message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Redirect when authenticated (after state has committed)
  React.useEffect(() => {
    if (isAuthenticated && user && !mfaRequired) {
      const from = (location.state as any)?.from?.pathname;
      const target = user.role === 'superadmin' ? (from || '/superadmin') : (from || '/dashboard');
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, user, navigate, location, mfaRequired]);

  // Check lockout timer
  React.useEffect(() => {
    if (!lockoutUntil) return;

    const checkLockout = () => {
      const now = new Date();
      const lockoutDate = new Date(lockoutUntil);
      if (now >= lockoutDate) {
        setLockoutUntil(null);
        setRemainingAttempts(null);
      }
    };

    const interval = setInterval(checkLockout, 1000);
    return () => clearInterval(interval);
  }, [lockoutUntil]);

  const getLockoutMessage = (): string | null => {
    if (!lockoutUntil) return null;
    const now = new Date();
    const lockoutDate = new Date(lockoutUntil);
    if (now >= lockoutDate) return null;

    const diffMs = lockoutDate.getTime() - now.getTime();
    const minutes = Math.ceil(diffMs / 60000);
    return t('security.lockout.message') + ` (${minutes} ${minutes !== 1 ? 'min' : 'min'})`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Check server-side lockout status BEFORE attempting login
    try {
      const lockoutStatus = await apiService.accountLockout.checkLocked(email);
      if (lockoutStatus.is_locked && lockoutStatus.locked_until) {
        setLockoutUntil(lockoutStatus.locked_until);
        setRemainingAttempts(0);
        setError(getLockoutMessage() || t('security.lockout.temporaryLock'));
        setIsLoading(false);
        return;
      }
      // Update remaining attempts from server
      setRemainingAttempts(lockoutStatus.remaining_attempts);
    } catch {
      // If lockout check fails, continue with login attempt
      console.warn('Could not check lockout status');
    }

    // Check if locally tracked lockout is still active
    if (lockoutUntil && new Date() < new Date(lockoutUntil)) {
      setError(getLockoutMessage() || t('security.lockout.temporaryLock'));
      setIsLoading(false);
      return;
    }

    try {
      await login(email, password);

      // Record successful login attempt
      try {
        await apiService.accountLockout.recordAttempt({
          email,
          success: true,
          userAgent: navigator.userAgent,
        });
      } catch {
        // Non-critical, continue
      }

      // After successful login, check if MFA is required
      try {
        const mfaStatus = await apiService.security.getMFAStatus();
        if (mfaStatus.mfaEnabled) {
          setMfaRequired(true);
          setIsLoading(false);
          return;
        }
      } catch {
        // MFA check failed, continue without MFA
      }
    } catch (err: any) {
      console.error('Login error:', err);

      let errorMessage = t('auth.login.errors.generic');
      let failureReason = 'unknown_error';

      if (err.message) {
        const message = err.message.toLowerCase();
        if (message.includes('invalid login credentials') || message.includes('invalid credentials')) {
          errorMessage = t('auth.login.errors.invalidCredentials');
          failureReason = 'invalid_password';
        } else if (message.includes('email not confirmed') || message.includes('email_not_confirmed')) {
          errorMessage = t('auth.login.errors.emailNotConfirmed');
          failureReason = 'email_not_confirmed';
        } else if (message.includes('user not found')) {
          errorMessage = t('auth.login.errors.userNotFound');
          failureReason = 'user_not_found';
        } else if (message.includes('too many requests')) {
          errorMessage = t('auth.login.errors.tooManyRequests');
          setLockoutUntil(new Date(Date.now() + 15 * 60 * 1000).toISOString());
          failureReason = 'rate_limited';
        } else {
          errorMessage = err.message;
        }
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }

      // Record failed login attempt server-side
      try {
        const lockoutResult = await apiService.accountLockout.recordAttempt({
          email,
          success: false,
          failureReason,
          userAgent: navigator.userAgent,
        });

        // Update UI with server-side lockout state
        setRemainingAttempts(lockoutResult.remaining_attempts);

        if (lockoutResult.is_locked && lockoutResult.locked_until) {
          setLockoutUntil(lockoutResult.locked_until);
          errorMessage = t('security.lockout.tooManyAttempts');
        } else if (lockoutResult.remaining_attempts <= 2 && lockoutResult.remaining_attempts > 0) {
          errorMessage += ' ' + t('security.lockout.remainingAttempts', { count: lockoutResult.remaining_attempts });
        }
      } catch {
        // If recording fails, fall back to local tracking
        if (remainingAttempts !== null && remainingAttempts > 0) {
          const newRemaining = remainingAttempts - 1;
          setRemainingAttempts(newRemaining);
          if (newRemaining <= 2 && newRemaining > 0) {
            errorMessage += ' ' + t('security.lockout.remainingAttempts', { count: newRemaining });
          } else if (newRemaining === 0) {
            const lockout = new Date(Date.now() + 15 * 60 * 1000).toISOString();
            setLockoutUntil(lockout);
            errorMessage = t('security.lockout.tooManyAttempts');
          }
        } else if (remainingAttempts === null) {
          setRemainingAttempts(4);
        }
      }

      setError(errorMessage);
    } finally {
      if (!mfaRequired) {
        setIsLoading(false);
      }
    }
  };

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMfaVerifying(true);

    try {
      const result = await apiService.security.verifyMFA(mfaCode);
      if (result.verified) {
        setMfaRequired(false);
        // Navigation will happen via the useEffect that watches isAuthenticated
      } else {
        setError(t('security.mfa.invalidCode'));
      }
    } catch (err: any) {
      setError(err.message || t('security.mfa.verifyError'));
    } finally {
      setMfaVerifying(false);
    }
  };

  const lockoutMessage = getLockoutMessage();
  const isLockedOut = !!lockoutMessage;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ffffff',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            {t('auth.login.title')}
          </Typography>

          {/* MFA Verification Step */}
          {mfaRequired ? (
            <>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <LockOutlinedIcon sx={{ fontSize: 28 }} />
              </Box>
              <Typography variant="h6" gutterBottom>
                {t('security.mfa.twoStepVerification')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                {t('security.mfa.enterCode')}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleMfaVerify} sx={{ width: '100%' }}>
                <TextField
                  required
                  fullWidth
                  label={t('security.mfa.codeLabel')}
                  value={mfaCode}
                  onChange={(e) => {
                    // Only allow digits and dashes (for backup codes)
                    const val = e.target.value.replace(/[^0-9A-Fa-f-]/g, '');
                    setMfaCode(val);
                  }}
                  placeholder="000000"
                  inputProps={{
                    maxLength: 9, // 6 digits or XXXX-XXXX backup code
                    autoComplete: 'one-time-code',
                    style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.3em' },
                  }}
                  autoFocus
                  disabled={mfaVerifying}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                  {t('security.mfa.codeHint')}
                </Typography>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={mfaVerifying || mfaCode.length < 6}
                >
                  {mfaVerifying ? <CircularProgress size={24} color="inherit" /> : t('security.mfa.verify')}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => {
                    setMfaRequired(false);
                    setMfaCode('');
                    setError('');
                  }}
                  sx={{ mt: 1 }}
                >
                  {t('auth.login.backToLogin')}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                {t('auth.login.subtitle')}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}

              {successMessage && (
                <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                  {successMessage}
                </Alert>
              )}

              {/* Lockout Warning */}
              <Collapse in={isLockedOut}>
                <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
                  {lockoutMessage}
                </Alert>
              </Collapse>

              {/* Remaining attempts warning */}
              <Collapse in={!isLockedOut && remainingAttempts !== null && remainingAttempts <= 2 && remainingAttempts > 0}>
                <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
                  {t('security.lockout.attemptsRemaining', { count: remainingAttempts ?? 0 })}
                </Alert>
              </Collapse>

              {/* OAuth Buttons */}
              <OAuthButtons mode="login" onError={setError} />

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label={t('auth.login.email')}
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading || isLockedOut}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label={t('auth.login.password')}
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading || isLockedOut}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        value="remember"
                        color="primary"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                    }
                    label={t('auth.login.rememberMe')}
                  />
                  <MuiLink component={Link} to="/forgot-password" sx={{ textDecoration: 'none', fontSize: '0.875rem' }}>
                    {t('auth.login.forgotPassword')}
                  </MuiLink>
                </Box>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={isLoading || isLockedOut}
                >
                  {isLoading ? t('auth.login.submitting') : t('auth.login.submit')}
                </Button>
              </Box>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('auth.login.noAccount')}{' '}
                  <MuiLink component={Link} to="/signup" sx={{ textDecoration: 'none' }}>
                    {t('auth.login.signUp')}
                  </MuiLink>
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
